/**
 * BottomSheet
 * Mobile: slide-up sheet from bottom.
 * Desktop (>= 640px): centered modal.
 *
 * Uses JS media query + inline styles for responsive layout because
 * the project's theme.css uses `!important` on `.hidden`, which
 * breaks the `hidden sm:flex` pattern for toggling layouts.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export default function BottomSheet({ isOpen, onClose, title, children }: BottomSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 640px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {isDesktop ? (
        /* ── Desktop: centered modal ── */
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            pointerEvents: 'none',
          }}
        >
          <div
            ref={sheetRef}
            className="bg-primary rounded-2xl shadow-2xl animate-fade-in"
            style={{
              width: '100%',
              maxWidth: '576px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              pointerEvents: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-shrink-0 border-b border-border">
              <div className="flex items-center justify-between p-lg">
                <h3 className="text-lg font-semibold text-primary">{title}</h3>
                <button onClick={onClose} className="btn-icon">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-lg">
              {children}
            </div>
          </div>
        </div>
      ) : (
        /* ── Mobile: bottom sheet ── */
        <div
          ref={sheetRef}
          className="absolute bg-primary rounded-t-2xl shadow-2xl animate-slide-up"
          style={{
            bottom: 0,
            left: 0,
            right: 0,
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex justify-center pt-sm">
            <div className="w-10 h-1 rounded-full bg-secondary/30" />
          </div>

          <div className="flex-shrink-0 border-b border-border">
            <div className="flex items-center justify-between p-lg">
              <h3 className="text-lg font-semibold text-primary">{title}</h3>
              <button onClick={onClose} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-lg">
            {children}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
